import { Box, Link, Paper, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ReactNode } from "react";
import { updateFocus, updateMode } from "../../redux/slices/editor";
import { connect } from "react-redux";
import { selectVersions } from "../../redux/selectors/firebase";
import { getDatabase } from "@firebase/database";

const databaseSources: { [key: string]: string } = {
    develop: "https://pathway-develop-default-rtdb.europe-west1.firebasedatabase.app",
    staging: "https://pathway-staging.europe-west1.firebasedatabase.app",
    release: "https://pathway-release.europe-west1.firebasedatabase.app",
    restore: "https://pathway-temp-0.europe-west1.firebasedatabase.app"
}

const useStyles = makeStyles((theme) => ({
    footer: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        justifyContent: "flex-end"
    },
    page: {
        width: "210mm",
        height: "297mm",
        paddingTop: "12.7mm",
        paddingBottom: "12.7mm",
        margin: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        pageBreakInside: "avoid",
        pageBreakBefore: "always",
        pageBreakAfter: "always",
    },
    pagePrint: {
        width: "210mm",
        height: "297mm",
        paddingTop: "6mm",
        paddingBottom: "6mm",
        margin: "6mm",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        pageBreakInside: "avoid",
        pageBreakBefore: "always",
        pageBreakAfter: "always",
    },
    pageNo: {
        marginLeft: "auto",
        marginRight: "20mm",
        minHeight: "12pt",
        fontFamily: "inherit",
    },
    pageNoPrinted: {
        marginLeft: "auto",
        marginRight: "30mm",
        minHeight: "12pt",
        fontFamily: "inherit",
    },
    version: {
        textAlign: "center",
        minHeight: "12pt",
        fontFamily: "inherit",
    }
}));

type Versions = { [key: string]: { tag: string, timestamp: number, version: number } }

interface Props
{
    children: ReactNode | ReactNode[]
    page: number
    paragraphs?: { min: number, max: number };
    state?: string
    updateFocus: (focus: string) => void
    updateMode: (mode: string) => void
    versions: Versions
}

function Page({ children, page, paragraphs, state, updateFocus, updateMode, versions }: Props)
{
    const classes = useStyles();
    const databaseURL = getDatabase().app.options.databaseURL;
    const deployment = Object.keys(databaseSources).find(key => databaseSources[key] === databaseURL) || "develop";
    const versionKeys = Object.keys(versions);
    const versionDetails = versions[versionKeys[versionKeys.length - 1]];
    const versionDate = new Date(versionDetails?.timestamp).toLocaleDateString();
    const versionTime = new Date(versionDetails?.timestamp).toLocaleTimeString();
    const version = versionDetails ?
        `v${versionDetails.version} ${versionTime} ${versionDate} ${deployment}` :
        "Unknown version";
    const handlePageLinkClick = () =>
    {
        if (state)
        {
            updateMode("graph");
            updateFocus(state);
        }
    };

    const formattedPageNo = `Page ${page}`;
    const paragraphsText = paragraphs ? paragraphs.min !== paragraphs.max ? `${paragraphs.min} - ${paragraphs.max}` : `${paragraphs.min}` : ""
    const pageText = paragraphs ? `${formattedPageNo} (${paragraphsText})` : formattedPageNo;

    return <div id={`page${page}`}>
        <Box display='block' displayPrint='none'>
            <Paper className={`NoExport ${classes.page}`} >
                {page > 1 &&
                    <Typography className={`NoExport ${classes.pageNo}`}>
                        {state ?
                            <Link onClick={handlePageLinkClick} sx={{ cursor: "pointer" }} title={state}>
                                {pageText}
                            </Link> :
                            pageText
                        }
                    </Typography>}
                {children}
                <Box className={classes.footer}>
                    <Typography className={classes.version}>{version}</Typography>
                </Box>
            </Paper >
        </Box>
        <Box display='none' displayPrint='block'>
            <Box className={classes.pagePrint}>
                {page > 1 &&
                    <Typography className={`PageNumber ${classes.pageNoPrinted}`}>{pageText}</Typography>
                }
                {children}
                <Box className={classes.footer}>
                    <Typography className={`Version ${classes.version}`}>{version}</Typography>
                </Box>
            </Box>
        </Box>
    </div>
}
const mapStateToProps = (store: never) => ({
    versions: selectVersions(store) || {}
});

export default connect(mapStateToProps, {
    updateFocus,
    updateMode
})(Page);
